import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Card, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputAdornment, InputLabel, Link,
  MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead,
  TablePagination, TableRow, TableSortLabel, TextField, Tooltip, Typography, makeStyles,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';
import { formatDate, formatMinorMoney, formatMoney, formatRenewalDays } from './commercialFormatters';
import {
  CommercialEmpty, CommercialError, CommercialLoading, CommercialPage, CommercialPageHeader,
} from './CommercialUi';
import { CommercialSectionNavigation, CommercialTaskGuide } from './CommercialNavigation';
import { getWorkflowActionState } from './commercialWorkflowState';
import CommercialCommitmentDialog from './CommercialCommitmentDialog';
import CommercialImportWorkflow from './CommercialImportWorkflow';
import {
  agreementCanCorrect, agreementLifecycleStatus, agreementNextAction, paymentProgress,
} from './commercialCommitmentState';
import {
  agreementMatchesQueue, buildAgreementRows, closedRenewalRowState, commercialFilterPreset,
  customerMatchesSignal, nextCommercialSort, renewalIsActionable, sectionRequiresDashboard,
  sortCommercialRows,
} from './commercialDataPageState';

const customerBlank = { name: '', country: '', status: 'ACTIVE' };
const normalized = value => String(value || '').trim().toLowerCase();
const lifecycleOrder = { PLAN_TERMS: 0, SCHEDULED: 1, APPLY_TO_MRR: 2, COMPLETED: 3, CANCELLED: 4, CLOSED: 5 };
const paymentOrder = { UNPLANNED: 0, UNPAID: 1, PARTIALLY_PAID: 2, PAID: 3, CANCELLED: 4, CLOSED: 5 };
const dataPageGuidance = {
  customers: {
    title: 'Customers',
    when: 'You need to find a customer or start a new commercial relationship.',
    changes: 'Creating adds the customer record. Relationship, revenue, and billing work continues in Customer detail.',
    start: 'Search and open an existing customer, or create a customer when no record exists.',
  },
  renewals: {
    title: 'Agreements',
    when: 'A renewal is due or an agreement is waiting for terms, payment, or MRR recognition.',
    changes: 'Agreement action controls MRR recognition. Payment collection continues independently in Payments; renewal follow-up only requests a reminder.',
    start: 'Choose a queue, complete the revenue action when due, and use Manage payments or Renewal follow-up for those separate jobs.',
  },
  imports: {
    title: 'Data imports',
    when: 'You have a prepared customer, opportunity, revenue, or agreement file to load in bulk.',
    changes: 'Only mapped rows that pass server validation are applied when you confirm the import.',
    start: 'Choose the record type and file, then review the proposed column mapping.',
  },
};

const useStyles = makeStyles(theme => ({
  card: { minWidth: 0, overflow: 'hidden', width: '100%' },
  toolbar: {
    alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5), padding: theme.spacing(2),
    '& .MuiFormControl-root': { minWidth: 160 },
    [theme.breakpoints.down('xs')]: { alignItems: 'stretch', flexDirection: 'column', '& .MuiFormControl-root': { width: '100%' } },
  },
  table: { maxWidth: '100%', overflowX: 'auto' },
  dataTable: { minWidth: 920 },
  importIntro: { background: theme.palette.action.hover, borderRadius: theme.shape.borderRadius, marginBottom: theme.spacing(2), padding: theme.spacing(2) },
  importActions: { alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1.5) },
  resultSuccess: { color: theme.palette.success.dark },
  urgent: { color: theme.palette.error.main, fontWeight: 500 },
  rowMessage: { display: 'block', marginTop: theme.spacing(.5), maxWidth: 240 },
}));

const SortableTableCell = ({ align, label, sort, sortKey, onSort }) => (
  <TableCell align={align} sortDirection={sort.key === sortKey ? sort.direction : false}>
    <TableSortLabel
      active={sort.key === sortKey}
      direction={sort.key === sortKey ? sort.direction : 'asc'}
      onClick={() => onSort(sortKey)}
      aria-label={`Sort by ${label}`}
    >{label}</TableSortLabel>
  </TableCell>
);

export default function CommercialDataPage({ section }) {
  const classes = useStyles();
  const token = useTokenOps().account.accessToken;
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);
  const [dashboard, setDashboard] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState(null);
  const [revision, setRevision] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState(() => commercialFilterPreset(section, typeof window === 'undefined' ? '' : window.location.hash));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState({ key: null, direction: 'asc' });
  const [workflowPolicy, setWorkflowPolicy] = useState(null);
  const [rowMessages, setRowMessages] = useState({});
  const [workflowBusyId, setWorkflowBusyId] = useState(null);
  const [commitments, setCommitments] = useState([]);
  const [agreementsLoading, setAgreementsLoading] = useState(section === 'renewals');
  const [agreementsError, setAgreementsError] = useState(null);
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [commitmentForm, setCommitmentForm] = useState(null);
  const [recognitionForm, setRecognitionForm] = useState(null);
  const requiresDashboard = sectionRequiresDashboard(section);

  useEffect(() => {
    setSort({ key: null, direction: 'asc' });
  }, [section]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const applyPreset = () => { setFilter(commercialFilterPreset(section, window.location.hash)); setPage(0); };
    applyPreset();
    window.addEventListener('hashchange', applyPreset);
    return () => window.removeEventListener('hashchange', applyPreset);
  }, [section]);

  useEffect(() => {
    if (!requiresDashboard) return;
    setError(null);
    fetch(`${hostname}/commercial/dashboard?range=ALL`, { headers })
      .then(response => response.ok ? response.json() : Promise.reject(new Error(`Commercial data unavailable (${response.status})`)))
      .then(setDashboard)
      .catch(setError);
  }, [headers, requiresDashboard, revision]);
  useEffect(() => {
    if (section !== 'renewals') return;
    setAgreementsLoading(true);
    setAgreementsError(null);
    Promise.all([
      fetch(`${hostname}/commercial/workflows/RENEWAL_OUTREACH/policy`, { headers }).then(response => response.ok ? response.json() : null),
      fetch(`${hostname}/commercial/commitments`, { headers }).then(response => response.ok ? response.json() : Promise.reject(new Error(`Agreements could not be loaded (${response.status})`))),
      fetch(`${hostname}/commercial/features`, { headers }).then(response => response.ok ? response.json() : []),
    ]).then(([policy, commitmentRows, features]) => {
      setWorkflowPolicy(policy);
      setCommitments(commitmentRows);
      setFeatureCatalog(features);
    }).catch(setAgreementsError).finally(() => setAgreementsLoading(false));
  }, [headers, section, revision]);

  if (section === 'imports') return <CommercialPage>
    <CommercialPageHeader title="Data imports" description="Choose one workbook sheet, review every mapping and server validation, then confirm the exact dataset once." />
    <CommercialSectionNavigation area="operations" current="imports" />
    <CommercialTaskGuide {...dataPageGuidance.imports} />
    {error && <CommercialError error={error} />}
    <CommercialImportWorkflow headers={headers} hostname={hostname} onImported={() => setRevision(value => value + 1)} />
  </CommercialPage>;

  if (error && !dashboard) return <CommercialPage><CommercialPageHeader title="Commercial data" description="Customer, renewal and agreement records." /><CommercialError error={error} /></CommercialPage>;
  if (!dashboard) return <CommercialPage><CommercialPageHeader title="Commercial data" description="Customer, renewal and agreement records." /><CommercialLoading /></CommercialPage>;
  if (section === 'renewals' && agreementsError) return <CommercialPage><CommercialPageHeader title="Agreements" description="Renewals, expansions and new contracts." /><CommercialError error={agreementsError} /></CommercialPage>;
  if (section === 'renewals' && agreementsLoading) return <CommercialPage><CommercialPageHeader title="Agreements" description="Renewals, expansions and new contracts." /><CommercialLoading /></CommercialPage>;

  const allRows = section === 'customers' ? dashboard.customers || [] : buildAgreementRows(dashboard.renewals, commitments);
  const filteredRows = allRows.filter(row => {
    if (search && !normalized(`${row.name} ${row.country} ${row.kind}`).includes(normalized(search))) return false;
    if (section === 'customers' && ['ACTIVE', 'INACTIVE'].includes(filter) && row.status !== filter) return false;
    if (section === 'customers' && !customerMatchesSignal(row, filter)) return false;
    if (section === 'renewals' && !agreementMatchesQueue(row, filter)) return false;
    return true;
  });
  const isCustomers = section === 'customers';
  const renewalAction = getWorkflowActionState(workflowPolicy, 'Send reminder', 'RENEWAL_OUTREACH');
  const commitmentById = id => commitments.find(item => item.id === id);
  const sortedRows = sortCommercialRows(filteredRows, sort, (row, key) => {
    if (key === 'lifecycle') {
      const closedState = closedRenewalRowState(row);
      const actionId = closedState ? 'CLOSED' : agreementNextAction(commitmentById(row.commercialCommitmentId)).id;
      return lifecycleOrder[actionId] ?? Number.MAX_SAFE_INTEGER;
    }
    if (key === 'payment') {
      const closedState = closedRenewalRowState(row);
      const paymentStatus = closedState ? 'CLOSED' : commitmentById(row.commercialCommitmentId)?.paymentStatus || 'UNPLANNED';
      return paymentOrder[paymentStatus] ?? Number.MAX_SAFE_INTEGER;
    }
    return row[key];
  });
  const visibleRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const requestSort = key => {
    setSort(current => nextCommercialSort(current, key));
    setPage(0);
  };
  const setRowMessage = (rowId, message) => setRowMessages(current => ({ ...current, [rowId]: message }));
  const planTerms = row => setCommitmentForm({
    ...row,
    renewalId: row.id,
    id: row.customerId,
    currentMrr: dashboard.customers?.find(customer => customer.id === row.customerId)?.currentMrr || Number(row.arrAtRisk) / 12,
  });
  const reviewTerms = (row, commitment) => {
    const customer = dashboard.customers?.find(item => item.id === row.customerId);
    setCommitmentForm({
      ...row,
      id: row.customerId,
      name: customer?.name || row.name,
      currentMrr: customer?.currentMrr ?? commitment.previousMrr,
      agreement: commitment,
      renewalId: commitment.renewalId || (row.rowType === 'RENEWAL_SCHEDULE' ? row.id : null),
    });
  };
  const requestOutreach = async row => {
    setWorkflowBusyId(`outreach-${row.id}`);
    setRowMessage(row.id, null);
    try {
      const response = await fetch(`${hostname}/commercial/renewals/${row.id}/outreach`, {
        method: 'POST', headers, body: JSON.stringify({ requestId: `renewal-${row.id}-${Date.now()}` }),
      });
      const run = await response.json().catch(() => ({}));
      const failed = run.status === 'FAILED' || run.status === 'BLOCKED';
      setRowMessage(row.id, response.ok && !failed
        ? { scope: 'renewal', text: `Reminder ${String(run.status || 'requested').toLowerCase()}.` }
        : { error: true, scope: 'renewal', text: run.error || `Reminder request failed (${response.status}).` });
    } catch (cause) {
      setRowMessage(row.id, { error: true, scope: 'renewal', text: cause.message || 'Reminder request failed.' });
    } finally { setWorkflowBusyId(null); }
  };
  const applyToMrr = async (row, commitment) => {
    setWorkflowBusyId(commitment.id);
    setRowMessage(row.id, null);
    try {
      const response = await fetch(`${hostname}/commercial/commitments/${commitment.id}/recognize`, {
        method: 'POST', headers,
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(result.message || `Could not apply agreement to MRR (${response.status}).`);
      setRowMessage(row.id, { scope: 'agreement', text: `Applied to MRR as ${result.revenueEvent?.type || 'renewal'}.` });
      setRevision(value => value + 1);
      return true;
    } catch (cause) {
      setRowMessage(row.id, { error: true, scope: 'agreement', text: cause.message || 'Could not apply agreement to MRR.' });
      return false;
    } finally { setWorkflowBusyId(null); }
  };

  return <CommercialPage>
    <CommercialPageHeader
      title={isCustomers ? 'Customers' : 'Agreements'}
      description={isCustomers ? 'Open a customer to manage their relationship, revenue and billing.' : 'Renewals, expansions and new contracts with parallel revenue recognition and payment collection.'}
      actions={isCustomers
        ? <Button variant="contained" color="primary" onClick={() => { setFormError(null); setForm(customerBlank); }}>Create customer</Button>
        : null}
    />
    <CommercialTaskGuide {...dataPageGuidance[section]} />
    {error && dashboard && <CommercialError error={error} />}
    <Card className={classes.card}>
      <div className={classes.toolbar}>
        <TextField id={`commercial-${section}-search`} size="small" variant="outlined" label={`Search ${isCustomers ? 'customers' : 'agreements'}`} value={search} onChange={event => { setSearch(event.target.value); setPage(0); }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
        <FormControl size="small" variant="outlined"><InputLabel>{isCustomers ? 'Status' : 'Queue'}</InputLabel><Select label={isCustomers ? 'Status' : 'Queue'} value={filter} onChange={event => { setFilter(event.target.value); setPage(0); }}><MenuItem value="ALL">All {isCustomers ? 'customers' : 'agreements'}</MenuItem>{isCustomers ? [<MenuItem value="ACTIVE" key="ACTIVE">Active</MenuItem>, <MenuItem value="INACTIVE" key="INACTIVE">Inactive</MenuItem>, <MenuItem value="NEGATIVE" key="NEGATIVE">MRR decreased</MenuItem>] : [<MenuItem value="UPCOMING" key="UPCOMING">Due within 90 days</MenuItem>, <MenuItem value="URGENT" key="URGENT">Due within 30 days</MenuItem>, <MenuItem value="OVERDUE" key="OVERDUE">Overdue</MenuItem>]}</Select></FormControl>
        <Typography variant="body2" color="textSecondary">{filteredRows.length} of {allRows.length} records</Typography>
      </div>
      {!isCustomers && <Box px={2} pb={2}>
        <Typography variant="body2" color="textSecondary">
          Payment and MRR are intentionally separate controls. Renewal reminders are optional follow-up and never advance the agreement lifecycle.
        </Typography>
      </Box>}
      <TableContainer className={classes.table}>
        <Table className={classes.dataTable} size="small" aria-label={isCustomers ? 'Customers' : 'Agreements'}>
          <TableHead><TableRow>{isCustomers ? <>
            <SortableTableCell label="Customer" sortKey="name" sort={sort} onSort={requestSort} />
            <TableCell>Country</TableCell>
            <SortableTableCell label="Status" sortKey="status" sort={sort} onSort={requestSort} />
            <SortableTableCell align="right" label="MRR" sortKey="currentMrr" sort={sort} onSort={requestSort} />
            <SortableTableCell align="right" label="Historical revenue" sortKey="historicalRevenue" sort={sort} onSort={requestSort} />
          </> : <>
            <TableCell>Customer</TableCell>
            <SortableTableCell label="Agreement due" sortKey="renewalDate" sort={sort} onSort={requestSort} />
            <SortableTableCell align="right" label="Value" sortKey="arrAtRisk" sort={sort} onSort={requestSort} />
            <SortableTableCell label="Lifecycle" sortKey="lifecycle" sort={sort} onSort={requestSort} />
            <SortableTableCell label="Payment" sortKey="payment" sort={sort} onSort={requestSort} />
            <TableCell align="right">Renewal follow-up</TableCell>
            <TableCell align="right">Agreement action</TableCell>
          </>}</TableRow></TableHead>
          <TableBody>
            {isCustomers ? visibleRows.map(row => <TableRow hover key={row.id}><TableCell><Link href={`#/commercial/${row.id}/show`}>{row.name}</Link></TableCell><TableCell>{row.country || 'Not recorded'}</TableCell><TableCell><Chip size="small" variant="outlined" label={row.status === 'ACTIVE' ? 'Active' : 'Inactive'} /></TableCell><TableCell align="right">{formatMoney(row.currentMrr)}</TableCell><TableCell align="right">{formatMoney(row.historicalRevenue)}</TableCell></TableRow>) : visibleRows.map(row => {
              const commitment = commitmentById(row.commercialCommitmentId);
              const progress = paymentProgress(commitment?.installments || [], commitment?.totalAmountMinor);
              const totalMinor = Number(commitment?.totalAmountMinor || 0);
              const paidMinor = Number(commitment?.paidMinor == null ? progress.paidMinor : commitment.paidMinor);
              const outstandingMinor = Number(commitment?.outstandingMinor == null ? Math.max(0, totalMinor - paidMinor) : commitment.outstandingMinor);
              const closedState = commitment ? null : closedRenewalRowState(row);
              const nextAction = closedState ? null : agreementNextAction(commitment);
              const lifecycle = closedState?.lifecycle || agreementLifecycleStatus(commitment);
              const rowMessage = rowMessages[row.id];
              const isRenewalRow = row.rowType === 'RENEWAL_SCHEDULE';
              const actionableRenewal = renewalIsActionable(row);
              return <TableRow hover key={row.id}>
                <TableCell>
                  <Link href={`#/commercial/${row.customerId}/show`}>{row.name}</Link>
                  {isRenewalRow && <Typography variant="caption" display="block" className={actionableRenewal && row.daysUntilRenewal <= 30 ? classes.urgent : undefined}>{actionableRenewal ? formatRenewalDays(row.daysUntilRenewal) : 'Renewal closed'}</Typography>}
                </TableCell>
                <TableCell><Typography variant="body2">{String(row.kind || 'RENEWAL').replace(/_/g, ' ')}</Typography><Typography variant="caption" display="block" color="textSecondary">{isRenewalRow ? `Due ${formatDate(row.renewalDate)} · ${row.status || row.riskBucket}` : `MRR date ${formatDate(row.renewalDate)} · ${row.status}`}</Typography></TableCell>
                <TableCell align="right">{formatMoney(row.arrAtRisk)}</TableCell>
                <TableCell>{closedState
                  ? <><Chip size="small" variant="outlined" label={lifecycle.label} /><Typography variant="caption" display="block" color="textSecondary">{lifecycle.detail}</Typography></>
                  : <Tooltip arrow title="Revenue: plan terms → Apply to MRR. Collection is tracked separately in Payments."><Box display="inline-block"><Chip size="small" color={commitment?.recognizedAt ? 'primary' : 'default'} variant={commitment?.recognizedAt ? 'default' : 'outlined'} label={lifecycle.label} /><Typography variant="caption" display="block" color="textSecondary">{lifecycle.detail}</Typography></Box></Tooltip>}
                </TableCell>
                <TableCell>{closedState
                  ? <Typography variant="caption" color="textSecondary">{closedState.paymentLabel}</Typography>
                  : commitment ? <><Chip size="small" variant={commitment.paymentStatus === 'PAID' ? 'default' : 'outlined'} color={commitment.paymentStatus === 'PAID' ? 'primary' : 'default'} label={commitment.paymentStatus === 'PAID_UNKNOWN' ? 'PAID · DATE UNKNOWN' : commitment.paymentStatus.replaceAll('_', ' ')} /><Typography variant="caption" display="block" color="textSecondary">{formatMinorMoney(paidMinor, commitment.currency)} paid · {formatMinorMoney(outstandingMinor, commitment.currency)} remaining</Typography><Button size="small" color="primary" href={`#/commercial-payments?commitmentId=${commitment.id}`}>Manage payments</Button></> : <Typography variant="caption" color="textSecondary">Terms not planned</Typography>}
                </TableCell>
                <TableCell align="right">
                  {actionableRenewal
                    ? <Box display="flex" flexDirection="column" alignItems="flex-end">
                      <Button size="small" color="primary" disabled={!renewalAction.allowed || workflowBusyId === `outreach-${row.id}`} onClick={() => requestOutreach(row)}>{workflowBusyId === `outreach-${row.id}` ? 'Requesting…' : renewalAction.label}</Button>
                      {rowMessage?.scope === 'renewal' && <Typography className={classes.rowMessage} variant="caption" color={rowMessage.error ? 'error' : 'primary'} role={rowMessage.error ? 'alert' : 'status'}>{rowMessage.text}</Typography>}
                    </Box>
                    : <Typography variant="caption" color="textSecondary">{isRenewalRow ? 'No reminder action' : 'Not a renewal'}</Typography>}
                </TableCell>
                <TableCell align="right">
                  {closedState ? <Typography variant="caption" color="textSecondary">History only</Typography> : <Box display="flex" flexDirection="column" alignItems="flex-end">
                    <Button size="small" color="primary" variant={nextAction.disabled ? 'text' : 'contained'} disabled={nextAction.disabled || workflowBusyId === commitment?.id} onClick={() => {
                      if (nextAction.id === 'PLAN_TERMS') planTerms(row);
                      if (nextAction.id === 'APPLY_TO_MRR') { setRowMessage(row.id, null); setRecognitionForm({ row, commitment, currentMrr: dashboard.customers?.find(customer => customer.id === row.customerId)?.currentMrr ?? commitment.previousMrr }); }
                    }}>{workflowBusyId === commitment?.id ? 'Applying…' : nextAction.label}</Button>
                    {agreementCanCorrect(commitment) && <Button size="small" color="primary" onClick={() => reviewTerms(row, commitment)}>Review terms</Button>}
                    {rowMessage?.scope === 'agreement' && <Typography className={classes.rowMessage} variant="caption" color={rowMessage.error ? 'error' : 'primary'} role={rowMessage.error ? 'alert' : 'status'}>{rowMessage.text}</Typography>}
                  </Box>}
                </TableCell>
              </TableRow>;
            })}
            {visibleRows.length === 0 && <TableRow><TableCell colSpan={isCustomers ? 5 : 7}><CommercialEmpty title={`No ${isCustomers ? 'customers' : 'agreements'} found`} description="Adjust the search or filter to see more records." /></TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination component="div" count={filteredRows.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_, nextPage) => setPage(nextPage)} onRowsPerPageChange={event => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50]} />
    </Card>

    {form && <Dialog open fullWidth maxWidth="sm" onClose={() => setForm(null)}><DialogTitle>New customer</DialogTitle><DialogContent><Box display="grid" gridGap={16} pt={1}><TextField autoFocus variant="outlined" required label="Customer name" value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} /><TextField variant="outlined" label="Country" value={form.country} onChange={event => setForm({ ...form, country: event.target.value })} /><FormControl variant="outlined"><InputLabel>Status</InputLabel><Select label="Status" value={form.status} onChange={event => setForm({ ...form, status: event.target.value })}><MenuItem value="ACTIVE">Active</MenuItem><MenuItem value="INACTIVE">Inactive</MenuItem></Select></FormControl>{formError && <Typography color="error" role="alert">{formError}</Typography>}</Box></DialogContent><DialogActions><Button onClick={() => setForm(null)}>Cancel</Button><Button disabled={!form.name.trim()} variant="contained" color="primary" onClick={async () => { setFormError(null); try { const response = await fetch(`${hostname}/commercial/customers`, { method: 'POST', headers, body: JSON.stringify(form) }); const result = await response.json().catch(() => ({})); if (!response.ok) throw new Error(result.message || `Customer could not be created (${response.status}).`); setForm(null); setRevision(value => value + 1); } catch (cause) { setFormError(cause.message || 'Customer could not be created.'); } }}>Create customer</Button></DialogActions></Dialog>}
    {commitmentForm && <CommercialCommitmentDialog customer={commitmentForm} renewalId={commitmentForm.renewalId} initialKind={commitmentForm.agreement?.kind || 'RENEWAL'} agreement={commitmentForm.agreement} featureCatalog={featureCatalog} headers={headers} hostname={hostname} onClose={() => setCommitmentForm(null)} onSaved={() => { setCommitmentForm(null); setRevision(value => value + 1); }} />}
    {recognitionForm && <Dialog open fullWidth maxWidth="sm" onClose={() => !workflowBusyId && setRecognitionForm(null)}>
      <DialogTitle>Apply agreement to MRR?</DialogTitle>
      <DialogContent>
        <Typography paragraph><strong>{recognitionForm.row.name}</strong> · {recognitionForm.commitment.kind}</Typography>
        <Typography variant="body2">Recognition date: <strong>{formatDate(recognitionForm.commitment.recognitionDate)}</strong></Typography>
        <Typography variant="body2">Reported MRR: <strong>{formatMoney(recognitionForm.currentMrr)} → {formatMoney(recognitionForm.commitment.resultingMrr)}</strong></Typography>
        <Box mt={2}><Typography variant="body2" color="textSecondary">Confirming creates the revenue movement and changes reported MRR. Cash collection is tracked separately in Payments and can continue after this step.</Typography></Box>
        {rowMessages[recognitionForm.row.id] && <Box mt={2}><Typography color={rowMessages[recognitionForm.row.id].error ? 'error' : 'primary'} role={rowMessages[recognitionForm.row.id].error ? 'alert' : 'status'}>{rowMessages[recognitionForm.row.id].text}</Typography></Box>}
      </DialogContent>
      <DialogActions><Button disabled={workflowBusyId === recognitionForm.commitment.id} onClick={() => setRecognitionForm(null)}>Cancel</Button><Button color="primary" variant="contained" disabled={workflowBusyId === recognitionForm.commitment.id} onClick={async () => { if (await applyToMrr(recognitionForm.row, recognitionForm.commitment)) setRecognitionForm(null); }}>{workflowBusyId === recognitionForm.commitment.id ? 'Applying…' : 'Confirm and apply to MRR'}</Button></DialogActions>
    </Dialog>}
  </CommercialPage>;
}
